import { useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import { useThemeColor } from '../../hooks/useThemeColor';
import { isChatZoomSupported } from '../../hooks/useChatZoom';

const ChatZoomControls = ({ zoom, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut }) => {
  const { t } = useTranslation();
  const { primaryColor, hoverColor, focusColor } = useThemeColor();
  const [open, setOpen] = useState(false);
  const [supported] = useState(isChatZoomSupported);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!supported) return null;

  const themeVars = {
    '--zoom-accent': primaryColor,
    '--zoom-accent-hover': hoverColor,
    '--zoom-accent-soft': focusColor,
  };
  const selectable = 'hover:bg-[var(--zoom-accent-soft)] hover:text-[var(--zoom-accent)] active:bg-[var(--zoom-accent-soft)] active:text-[var(--zoom-accent-hover)] focus-visible:outline-none focus-visible:bg-[var(--zoom-accent-soft)] focus-visible:text-[var(--zoom-accent)]';
  const iconButton = `w-8 h-8 flex items-center justify-center rounded-full text-[#6B7280] transition-colors ${selectable} disabled:opacity-40 disabled:pointer-events-none`;
  const isZoomed = zoom !== 1;

  return (
    <div className="relative h-0 flex-shrink-0">
      <div ref={rootRef} className="absolute right-0 bottom-4 z-20" style={themeVars}>
        {open ? (
          <div className="flex flex-col items-center gap-0.5 p-1 bg-white border border-[#E5E7EB] rounded-full shadow-sm mr-2">
            <button type="button" onClick={zoomIn} disabled={!canZoomIn} className={iconButton} title={t('chatZoom.zoomIn')} aria-label={t('chatZoom.zoomIn')}>
              <ZoomIn size={16} />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className={`w-8 h-6 flex items-center justify-center rounded-full text-[11px] font-medium focus:outline-none ${isZoomed ? 'text-[var(--zoom-accent)]' : 'text-[#6B7280]'}`}
              aria-label={t('chatZoom.reset')}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button type="button" onClick={zoomOut} disabled={!canZoomOut} className={iconButton} title={t('chatZoom.zoomOut')} aria-label={t('chatZoom.zoomOut')}>
              <ZoomOut size={16} />
            </button>
            <div className="w-5 h-px bg-[#E5E7EB] my-0.5" />
            <button type="button" onClick={() => setOpen(false)} className={iconButton} title={t('chatZoom.close')} aria-label={t('chatZoom.close')}>
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={`w-6 h-10 flex items-center justify-center bg-white border border-r-0 border-[#E5E7EB] rounded-l-full shadow-sm transition-all hover:w-7 ${isZoomed ? 'text-[var(--zoom-accent)]' : 'text-[#6B7280]'} ${selectable}`}
            title={t('chatZoom.open')}
            aria-label={t('chatZoom.open')}
          >
            <ZoomIn size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatZoomControls;
